import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import marketplaceService from '../../../services/marketplaceService';

const MyOffers = ({ onRefresh }) => {
  const { user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOffers();
  }, []);

  const loadOffers = async () => {
    setLoading(true);
    const { data, error } = await marketplaceService?.getUserOffers(user?.id);
    if (!error) {
      setOffers(data || []);
    }
    setLoading(false);
  };

  const handleWithdrawOffer = async (offerId) => {
    if (!confirm('Are you sure you want to withdraw this offer?')) return;
    
    const { error } = await marketplaceService?.updateOffer(offerId, { status: 'withdrawn' });
    if (!error) {
      loadOffers();
      onRefresh?.();
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      countered: 'bg-blue-100 text-blue-800',
      expired: 'bg-gray-100 text-gray-800',
      withdrawn: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles?.[status] || styles?.pending}`}>
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[1, 2, 3]?.map(i => (
          <div key={i} className="bg-card border border-border rounded-lg h-32" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {offers?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="MessageSquare" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">No offers yet</h3>
          <p className="text-muted-foreground">Browse the marketplace and make your first offer</p>
        </div>
      ) : (
        offers?.map(offer => (
          <div key={offer?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start space-x-4">
              {/* Listing Image */}
              <div className="w-20 h-20 bg-muted rounded-lg flex-shrink-0 overflow-hidden">
                {offer?.listing?.featured_image_url ? (
                  <img
                    src={offer?.listing?.featured_image_url}
                    alt={offer?.listing?.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="Image" size={24} className="text-muted-foreground" />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">
                      {offer?.listing?.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Seller: {offer?.seller?.full_name}
                    </p>
                  </div>
                  {getStatusBadge(offer?.status)}
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Your Offer</p>
                    <p className="text-lg font-bold text-primary">
                      ${parseFloat(offer?.offer_amount || 0)?.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Asking Price</p>
                    <p className="text-lg font-semibold text-foreground">
                      ${parseFloat(offer?.listing?.asking_price || 0)?.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Submitted</p>
                    <p className="text-sm font-medium text-foreground">
                      {new Date(offer?.created_at)?.toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {offer?.message && (
                  <div className="mt-4 p-3 bg-muted/20 rounded-lg">
                    <p className="text-sm text-muted-foreground italic">"{offer?.message}"</p>
                  </div>
                )}

                {offer?.counter_offer_amount && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-900">
                      Counter Offer: ${parseFloat(offer?.counter_offer_amount)?.toFixed(2)}
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              {offer?.status === 'pending' && (
                <Button
                  size="sm"
                  variant="outline"
                  iconName="X"
                  onClick={() => handleWithdrawOffer(offer?.id)}
                >
                  Withdraw
                </Button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default MyOffers;
